import { useMemo, useState } from 'react';
import type { Lead, Meta } from '@shared/types';
import { activity, changePct, compareDays, compareWeeks, funnel, FUNNEL, type Activity, type DayCompare, type Funnel, type SourceRow, type WeekCompare } from '@shared/stats';
import { formatDateTime } from '@shared/dates';
import { useStageEvents, useStages } from '../store';
import { useMinuteClock } from '../clock';
import { inPeriod, type Period } from '../filter';

// One hue for "now", a lighter step of it for the period it's compared with (validated as an ordered pair).
const NOW_COLOR = '#2a78d6';
const BEFORE_COLOR = '#86b6ef';

const fmt = (n: number) => n.toLocaleString('he-IL');
const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—');

/** /stats: the funnel, today's hours against the same hours a week ago, this week against last week (and by source). */
export function StatsPage({ leads, meta }: { leads: Lead[]; meta: Meta }) {
  const now = useMinuteClock();
  const week = useMemo(() => compareWeeks(leads, now), [leads, now]);
  const day = useMemo(() => compareDays(leads, now), [leads, now]);
  return (
    <div className="mx-auto grid max-w-4xl grid-cols-[minmax(0,1fr)] gap-4 p-4">
      <FunnelSection leads={leads} meta={meta} now={now} />
      <DaySection d={day} />
      <WeekSection w={week} />
    </div>
  );
}

function Change({ current, previous }: { current: number; previous: number }) {
  const c = changePct(current, previous);
  if (c === null) return <span className="text-ink-3">—</span>;
  return <span className="tabular-nums" dir="ltr">{c > 0 ? '▲' : c < 0 ? '▼' : ''} {c > 0 ? '+' : ''}{c}%</span>;
}

function Legend({ now, before }: { now: string; before: string }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-2">
      <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: NOW_COLOR }} />{now}</span>
      <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: BEFORE_COLOR }} />{before}</span>
    </div>
  );
}

function Hero({ current, previous, nowLabel, beforeLabel }: { current: number; previous: number; nowLabel: string; beforeLabel: string }) {
  return (
    <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
      <div><div className="text-xs text-ink-3">{nowLabel}</div><div className="text-3xl font-bold tabular-nums">{fmt(current)}</div></div>
      <div><div className="text-xs text-ink-3">{beforeLabel}</div><div className="text-xl font-semibold tabular-nums text-ink-2">{fmt(previous)}</div></div>
      <div className="pb-1 text-sm font-semibold"><Change current={current} previous={previous} /></div>
    </div>
  );
}

// ---- 7 days ----------------------------------------------------------------------------------------------------
function WeekSection({ w }: { w: WeekCompare }) {
  return (
    <section className="card grid gap-4 p-4">
      <h2 className="text-base font-semibold">7 הימים האחרונים מול 7 הימים שלפניהם</h2>
      <Hero current={w.current} previous={w.previous} nowLabel="7 ימים אחרונים" beforeLabel="7 ימים קודמים" />
      <p className="text-sm text-ink-2">
        ממומן: <b className="tabular-nums text-ink">{fmt(w.paid.current)}</b> ({pct(w.paid.current, w.current)}) מול{' '}
        <span className="tabular-nums">{fmt(w.paid.previous)}</span> ({pct(w.paid.previous, w.previous)}) <Change current={w.paid.current} previous={w.paid.previous} />
      </p>
      <Breakdown w={w} rows={w.sources} title="לפי מקור" column="מקור" />
      <Breakdown w={w} rows={w.mediums} title="לפי ערוץ" column="ערוץ" />
    </section>
  );
}

/** One breakdown of the week (by source, by medium): a bar pair per row, with an accessible table behind it. */
function Breakdown({ w, rows, title, column }: { w: WeekCompare; rows: SourceRow[]; title: string; column: string }) {
  const max = Math.max(1, ...rows.flatMap((s) => [s.current, s.previous]));
  return (
    <div className="grid gap-2">
      <h3 className="text-sm font-semibold">{title}</h3>
      {rows.length === 0 ? <p className="py-3 text-center text-sm text-ink-3">אין לידים בשבועיים האחרונים</p> : (
        <ul className="grid">
          {rows.map((s) => (
            <li key={s.source} className="grid gap-1.5 border-t border-line py-3">
              <div className="flex items-baseline justify-between gap-4">
                <span className="min-w-0 truncate font-medium" dir="auto">{s.source}</span>
                <span className="shrink-0 text-xs font-semibold"><Change current={s.current} previous={s.previous} /></span>
              </div>
              <SourceBar label="אחרונים" color={NOW_COLOR} n={s.current} share={pct(s.current, w.current)} max={max} strong />
              <SourceBar label="קודמים" color={BEFORE_COLOR} n={s.previous} share={pct(s.previous, w.previous)} max={max} />
            </li>
          ))}
        </ul>
      )}
      <div className="sr-only"><table>
        <caption>לידים {title}</caption>
        <thead><tr><th>{column}</th><th>7 ימים אחרונים</th><th>אחוז מהלידים</th><th>7 ימים קודמים</th><th>אחוז מהלידים</th></tr></thead>
        <tbody>{rows.map((s) => <tr key={s.source}><td>{s.source}</td><td>{s.current}</td><td>{pct(s.current, w.current)}</td><td>{s.previous}</td><td>{pct(s.previous, w.previous)}</td></tr>)}</tbody>
      </table></div>
    </div>
  );
}

/** One period of a source: its count (bar, against the biggest value in the table) and its share of that period's leads. */
function SourceBar({ label, color, n, share, max, strong }: { label: string; color: string; n: number; share: string; max: number; strong?: boolean }) {
  return (
    <div className="grid grid-cols-[3.75rem_minmax(0,1fr)_7.5rem] items-center gap-3 text-xs">
      <span className="text-ink-3">{label}</span>
      <span className="h-2 rounded-e bg-page"><span className="block h-2 rounded-e" style={{ width: `${(n / max) * 100}%`, minWidth: n ? 3 : 0, background: color }} /></span>
      <span className="text-end tabular-nums"><b className={strong ? 'text-ink' : 'font-semibold text-ink-2'}>{fmt(n)}</b><span className="text-ink-3"> · {share} מהלידים</span></span>
    </div>
  );
}

// ---- 24 hours ----------------------------------------------------------------------------------------------------
function DaySection({ d }: { d: DayCompare }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...d.buckets.flatMap((b) => [b.current, b.previous]));
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1];
  const H = 160;
  return (
    <section className="card grid gap-4 p-4">
      <h2 className="text-base font-semibold">24 השעות האחרונות מול אותן שעות לפני שבוע</h2>
      <Hero current={d.current} previous={d.previous} nowLabel="24 שעות אחרונות" beforeLabel="אותן שעות לפני שבוע" />
      <Legend now="24 שעות אחרונות" before="לפני שבוע" />
      {/* time reads left to right, like a clock face laid flat */}
      <div dir="ltr" className="relative select-none" onMouseLeave={() => setHover(null)}>
        <div className="relative ms-7" style={{ height: H }}>
          {ticks.map((t) => (
            <div key={t} className="absolute inset-x-0 border-t border-line" style={{ bottom: (t / top) * H }}>
              <span className="absolute -start-7 -translate-y-1/2 text-[10px] tabular-nums text-ink-3">{t}</span>
            </div>
          ))}
          <div className="absolute inset-0 grid grid-cols-12">
            {d.buckets.map((b, i) => (
              <div key={b.label} className={`relative flex items-end justify-center gap-[2px] px-[3px] ${hover === i ? 'bg-page' : ''}`}
                onMouseEnter={() => setHover(i)} onClick={() => setHover(i)}>
                <span className="w-full max-w-4 rounded-t" style={{ height: (b.previous / top) * H, minHeight: b.previous ? 2 : 0, background: BEFORE_COLOR }} />
                <span className="w-full max-w-4 rounded-t" style={{ height: (b.current / top) * H, minHeight: b.current ? 2 : 0, background: NOW_COLOR }} />
              </div>
            ))}
          </div>
          {hover !== null && (
            <div className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-lg border border-line bg-panel px-2.5 py-1.5 text-xs shadow-lg" dir="rtl"
              style={{ left: `${((hover + 0.5) / 12) * 100}%`, top: 0 }}>
              <div className="mb-0.5 font-semibold tabular-nums" dir="ltr">{d.buckets[hover].label}</div>
              <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: NOW_COLOR }} />אחרונות: <b className="tabular-nums">{d.buckets[hover].current}</b></div>
              <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: BEFORE_COLOR }} />לפני שבוע: <b className="tabular-nums">{d.buckets[hover].previous}</b></div>
            </div>
          )}
        </div>
        <div className="ms-7 grid grid-cols-12 pt-1 text-center text-[10px] tabular-nums text-ink-3">
          {/* on phones only the bucket's starting hour fits; the tooltip has the range */}
          {d.buckets.map((b) => <span key={b.label}><span className="max-[520px]:hidden">{b.label}</span><span className="hidden max-[520px]:inline">{b.label.slice(0, 2)}</span></span>)}
        </div>
      </div>
      <div className="sr-only"><table>
        <caption>לידים לפי שעה</caption>
        <thead><tr><th>שעות</th><th>24 שעות אחרונות</th><th>לפני שבוע</th></tr></thead>
        <tbody>{d.buckets.map((b) => <tr key={b.label}><td>{b.label}</td><td>{b.current}</td><td>{b.previous}</td></tr>)}</tbody>
      </table></div>
    </section>
  );
}

/** 3–5 round gridline values from 0 up to at least max. */
function niceTicks(max: number): number[] {
  const step = [1, 2, 5, 10, 20, 25, 50, 100, 200, 500].find((s) => max / s <= 4) ?? Math.ceil(max / 4);
  const out: number[] = [];
  for (let v = 0; v < max + step; v += step) out.push(v);
  return out;
}

// ---- funnel ---------------------------------------------------------------------------------------------------
const FUNNEL_PERIODS: Array<[Period, string]> = [['7d', '7 ימים'], ['30d', '30 ימים'], ['all', 'הכל']];

type FunnelView = 'cohort' | 'activity';
const FUNNEL_VIEWS: Array<[FunnelView, string]> = [['cohort', 'מה קרה ללידים שנכנסו'], ['activity', 'מה קרה בתקופה']];
const periodFrom = (p: Period, now: number) => (p === 'all' ? 0 : now - (p === '7d' ? 7 : 30) * 86_400_000);

/**
 * Two readings of the same steps. "The leads that came in" follows the leads that arrived in the period to wherever they
 * are now; "what happened" counts the moves made during the period, whichever day the lead arrived.
 */
function FunnelSection({ leads, meta, now }: { leads: Lead[]; meta: Meta; now: number }) {
  const [period, setPeriod] = useState<Period>('30d');
  const [view, setView] = useState<FunnelView>('cohort');
  const { stageName } = useStages();
  const { data } = useStageEvents();
  const f = useMemo(() => funnel({
    leads: leads.filter((l) => inPeriod(l, period, now)), groups: meta.groups, stageName, events: data?.events ?? [], trackingSince: data?.since ?? null,
  }), [leads, period, now, meta.groups, stageName, data]);
  const a = useMemo(() => activity({ leads, groups: meta.groups, stageName, events: data?.events ?? [], from: periodFrom(period, now), to: now }),
    [leads, period, now, meta.groups, stageName, data]);
  const since = data?.since ? <span className="ltr">{formatDateTime(data.since)}</span> : null;
  const partial = !!data?.since && periodFrom(period, now) < Date.parse(data.since);
  return (
    <section className="card grid gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">משפך</h2>
        <Chips options={FUNNEL_PERIODS} value={period} onChange={setPeriod} label="תקופה" />
      </div>
      <Chips options={FUNNEL_VIEWS} value={view} onChange={setView} label="תצוגה" />
      {view === 'cohort' ? <FunnelRows f={f} /> : <ActivityRows a={a} />}
      <p className="text-xs text-ink-3">
        {view === 'cohort' ? <>
          הלידים שנכנסו בתקופה, ולאן הגיעו עד עכשיו (גם אם זה קרה אחרי התקופה). "הגיעו" סופר כל ליד שעבר בשלב, גם אם המשיך הלאה. אבוד = עבר ל"{FUNNEL.irrelevant}" אחרי שלב מכירה.{' '}
          {since
            ? <>המסלול המלא של כל ליד נרשם מאז {since} — {fmt(f.withPath)} מתוך {fmt(f.incoming)} הלידים בתקופה; לשאר ידוע רק חלק מהמסלול (לרוב הסטטוס הנוכחי).</>
            : 'רישום המסלול של הלידים מתחיל עכשיו; עד שיצטבר, כל ליד נספר לפי הסטטוס הנוכחי שלו.'}
        </> : <>
          כמה לידים עברו לכל שלב במהלך התקופה, לא משנה מתי נכנסו (ליד שנכנס לפני 9 ימים ונסגר היום נספר כאן ב"נסגר"). אבוד = עבר ל"{FUNNEL.irrelevant}" אחרי שלב מכירה.{' '}
          {since ? <>מבוסס על שינויי הסטטוס שנרשמו מאז {since}{partial ? ' — התקופה מתחילה לפני כן, אז החלק הראשון שלה חסר.' : '.'}</> : 'שינויי הסטטוס נרשמים מעכשיו; עדיין אין נתונים.'}
        </>}
      </p>
    </section>
  );
}

function Chips<T extends string>({ options, value, onChange, label }: { options: Array<[T, string]>; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div className="flex flex-wrap gap-1" role="radiogroup" aria-label={label}>
      {options.map(([v, text]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)}
          className={`rounded-full border px-3 py-1 text-xs ${value === v ? 'border-accent bg-accent-soft font-semibold text-accent' : 'border-line text-ink-2'}`}>{text}</button>
      ))}
    </div>
  );
}

/** Moves during the period: plain counts, bars against the biggest one (they are not shares of one whole). */
function ActivityRows({ a }: { a: Activity }) {
  // arrivals are a different kind of count (and much bigger): a number of their own, not on the moves' scale
  const max = Math.max(1, a.noAnswer, a.irrelevant, a.car, a.funding, a.won, a.lost);
  const Row = ({ label, n, note }: { label: string; n: number; note?: string }) => (
    <div className="grid gap-1 text-sm" title={`${label}: ${fmt(n)}`}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate">{label}{note && <span className="ms-1.5 text-[11px] text-ink-3">{note}</span>}</span>
        <b className="shrink-0 tabular-nums">{fmt(n)}</b>
      </div>
      <span className="block h-2.5 rounded-e bg-page"><span className="block h-2.5 rounded-e" style={{ width: `${(n / max) * 100}%`, minWidth: n ? 3 : 0, background: NOW_COLOR }} /></span>
    </div>
  );
  return (
    <div className="grid gap-4">
      <div className="flex items-baseline justify-between gap-3 text-sm"><span>נכנסו בתקופה</span><b className="tabular-nums">{fmt(a.arrived)}</b></div>
      <div className="grid gap-2">
        <h3 className="text-xs font-semibold text-ink-3">עברו בתקופה ל…</h3>
        <Row label={FUNNEL.noAnswer} n={a.noAnswer} />
        <Row label={FUNNEL.irrelevant} note="ישר מהסינון" n={a.irrelevant} />
        <Row label={FUNNEL.car} n={a.car} />
        <Row label="מימון" note={FUNNEL.funding} n={a.funding} />
      </div>
      <div className="grid gap-2">
        <h3 className="text-xs font-semibold text-ink-3">תוצאות בתקופה</h3>
        <Row label="נסגר" note="זכייה" n={a.won} />
        <Row label="אבוד" note="לא רלוונטי אחרי מכירה" n={a.lost} />
      </div>
    </div>
  );
}

function FunnelRows({ f }: { f: Funnel }) {
  // label and numbers on one line, the bar under them at full width (a side-by-side row left the bar a sliver on phones)
  const Row = ({ label, n, of, note }: { label: string; n: number; of: number; note?: string }) => (
    <div className="grid gap-1 text-sm" title={`${label}: ${fmt(n)} (${pct(n, of)})`}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate">{label}{note && <span className="ms-1.5 text-[11px] text-ink-3">{note}</span>}</span>
        <span className="shrink-0 tabular-nums"><b>{fmt(n)}</b><span className="ms-1.5 text-xs text-ink-2">{pct(n, of)}</span></span>
      </div>
      <span className="block h-2.5 rounded-e bg-page"><span className="block h-2.5 rounded-e" style={{ width: `${of ? (n / of) * 100 : 0}%`, minWidth: n ? 3 : 0, background: NOW_COLOR }} /></span>
    </div>
  );
  return (
    <div className="grid gap-4">
      <Row label="נכנסו" n={f.incoming} of={f.incoming} />
      <div className="grid gap-2">
        <h3 className="text-xs font-semibold text-ink-3">הגיעו ל… (מתוך הנכנסים)</h3>
        <Row label={FUNNEL.noAnswer} n={f.noAnswer} of={f.incoming} />
        <Row label={FUNNEL.irrelevant} note="ישר מהסינון" n={f.irrelevant} of={f.incoming} />
        <Row label={FUNNEL.car} n={f.car} of={f.incoming} />
        <Row label="מימון" note={FUNNEL.funding} n={f.funding} of={f.incoming} />
      </div>
      <div className="grid gap-2">
        <h3 className="text-xs font-semibold text-ink-3">תוצאה — {fmt(f.sales)} לידים שהגיעו לשלב מכירה (רכב ספציפי / מימון)</h3>
        <Row label="נסגר" note="זכייה" n={f.won} of={f.sales} />
        <Row label="אבוד" note="לא רלוונטי אחרי מכירה" n={f.lost} of={f.sales} />
        <Row label="עדיין בתהליך" n={f.open} of={f.sales} />
      </div>
    </div>
  );
}
